import { ComponentKey } from 'enums/componentKey';
import { InputsConfig } from 'types/form/inputsConfig';
import { ProductFormFieldNames } from './productFormFieldNames';
import { ReferentialKey } from 'api/referential';
import { ProductFormValues } from './productFormValues';
import { TargetedSubscriberEnum, UnitEnum } from 'api/product';
import { AutoCompleteKey } from 'components/forms/AutoCompleteKey';

/** i18n keys of the targeted subscriber values, shared by the form and the product details. */
export const targetedSubscriberLabels: Record<TargetedSubscriberEnum, string> = {
  [TargetedSubscriberEnum.Any]: 'PRODUCT.TARGETED_SUBSCRIBERS.ANY',
  [TargetedSubscriberEnum.LegalEntity]: 'PRODUCT.TARGETED_SUBSCRIBERS.LEGAL_ENTITY',
  [TargetedSubscriberEnum.Individual]: 'PRODUCT.TARGETED_SUBSCRIBERS.INDIVIDUAL',
};

export const productInputsConfig: InputsConfig<keyof ProductFormValues> = {
  [ProductFormFieldNames.Label]: {
    componentKey: ComponentKey.InputText,
    name: ProductFormFieldNames.Label,
    type: 'text',
    required: true,
    label: 'PRODUCT.LABELS.LABEL',
    placeholder: 'PRODUCT.PLACEHOLDERS.LABEL',
  },
  [ProductFormFieldNames.Code]: {
    componentKey: ComponentKey.InputText,
    name: ProductFormFieldNames.Code,
    type: 'text',
    required: true,
    label: 'PRODUCT.LABELS.CODE',
    placeholder: 'PRODUCT.PLACEHOLDERS.CODE',
  },
  [ProductFormFieldNames.Summary]: {
    componentKey: ComponentKey.InputText,
    name: ProductFormFieldNames.Summary,
    type: 'text',
    required: true,
    label: 'PRODUCT.LABELS.SUMMARY',
    placeholder: 'PRODUCT.PLACEHOLDERS.SUMMARY',
  },
  [ProductFormFieldNames.FullDescription]: {
    componentKey: ComponentKey.InputText,
    name: ProductFormFieldNames.FullDescription,
    type: 'text',
    required: false,
    label: 'PRODUCT.LABELS.FULL_DESCRIPTION',
    placeholder: 'PRODUCT.PLACEHOLDERS.FULL_DESCRIPTION',
  },
  [ProductFormFieldNames.Logo]: {
    componentKey: ComponentKey.ImageUpload,
    name: ProductFormFieldNames.Logo,
    label: 'PRODUCT.LABELS.LOGO',
    placeholder: 'PRODUCT.PLACEHOLDERS.LOGO',
    dragPlaceholder: 'PRODUCT.PLACEHOLDERS.DRAG_LOGO',
    required: true,
  },
  [ProductFormFieldNames.BranchId]: {
    componentKey: ComponentKey.ReferentialDropdown,
    referentialKey: ReferentialKey.Branch,
    name: ProductFormFieldNames.BranchId,
    label: 'PRODUCT.LABELS.BRANCH',
    placeholder: 'PRODUCT.PLACEHOLDERS.BRANCH',
    required: true,
  },
  [ProductFormFieldNames.PersonId]: {
    componentKey: ComponentKey.AutoComplete,
    name: ProductFormFieldNames.PersonId,
    label: 'PRODUCT.LABELS.OWNER',
    placeholder: 'PRODUCT.PLACEHOLDERS.OWNER',
    autocompleteKey: AutoCompleteKey.ProductOwner,
    required: true,
    noResultsText: 'PRODUCT.MESSAGES.NOT_FOUND_OWNER',
  },
  [ProductFormFieldNames.NoticePeriodDuration]: {
    componentKey: ComponentKey.NumberWithVariableUnit,
    name: ProductFormFieldNames.NoticePeriodDuration,
    label: 'PRODUCT.LABELS.NOTICE_PERIOD_DURATION',
    placeholder: 'PRODUCT.PLACEHOLDERS.NOTICE_PERIOD_DURATION',
    required: true,
    units: [
      { key: UnitEnum.Day, label: 'COMMON.UNITS.DAYS' },
      { key: UnitEnum.Week, label: 'COMMON.UNITS.WEEKS' },
      { key: UnitEnum.Month, label: 'COMMON.UNITS.MONTHS' },
      { key: UnitEnum.Year, label: 'COMMON.UNITS.YEARS' },
    ],
    min: 0,
    step: 1,
  },
  [ProductFormFieldNames.IsTacitRenewal]: {
    componentKey: ComponentKey.Switch,
    name: ProductFormFieldNames.IsTacitRenewal,
    label: 'PRODUCT.LABELS.IS_TACIT_RENEWAL',
    falseLabel: 'PRODUCT.LABELS.IS_NOT_TACIT_RENEWAL',
  },
  [ProductFormFieldNames.IsFixedFormula]: {
    componentKey: ComponentKey.Switch,
    name: ProductFormFieldNames.IsFixedFormula,
    label: 'PRODUCT.LABELS.IS_FIXED_FORMULA',
    falseLabel: 'PRODUCT.LABELS.IS_NOT_FIXED_FORMULA',
  },
  [ProductFormFieldNames.TargetedSubscriber]: {
    componentKey: ComponentKey.Dropdown,
    name: ProductFormFieldNames.TargetedSubscriber,
    required: true,
    label: 'PRODUCT.LABELS.TARGETED_SUBSCRIBER',
    items: Object.values(TargetedSubscriberEnum).map(key => ({ key, label: targetedSubscriberLabels[key] })),
  },
  [ProductFormFieldNames.PlateformeIds]: {
    componentKey: ComponentKey.ReferentialTagInput,
    name: ProductFormFieldNames.PlateformeIds,
    referentialKey: ReferentialKey.Plateforme,
    label: 'COMMON.LABELS.PLATEFORMES',
    placeholder: 'COMMON.PLACEHOLDERS.PLATEFORMES',
    required: true,
  },
};
